import { useEffect, useState } from "react";
import { endpoints } from "@/lib/api";
import MetricCard from "@/components/MetricCard";
import GateCard from "@/components/GateCard";
import { fmtMoney, fmtPct } from "@/lib/format";
import { Link } from "react-router-dom";
import { AlertTriangle, ArrowUpRight, RefreshCcw } from "lucide-react";
import { toast } from "sonner";

export default function Overview() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const d = await endpoints.dashboard();
      setData(d);
    } catch (e) {
      toast.error("Failed to load dashboard");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const reseed = async () => {
    try {
      await endpoints.reseed();
      toast.success("Sample cohort reseeded");
      load();
    } catch {
      toast.error("Reseed failed");
    }
  };

  if (loading || !data) {
    return (
      <div className="container-hi py-10 text-[13px]" style={{ color: "var(--cream-muted)" }}>
        Loading cohort…
      </div>
    );
  }

  const behind = (data.participants || []).filter((p) => p.behind_flag);

  return (
    <div className="container-hi py-10" data-testid="overview-page">
      <header className="flex items-end justify-between gap-6 flex-wrap">
        <div>
          <div className="text-[11px] tracking-[0.22em] uppercase font-mono-num" style={{ color: "var(--gold)" }}>
            Overview
          </div>
          <h1 className="font-display text-[44px] leading-[1.05] mt-2" style={{ color: "var(--cream)" }}>
            Cohort at a glance
          </h1>
          <p className="mt-3 text-[13px] max-w-xl" style={{ color: "var(--cream-dim)" }}>
            Frozen Evidence gates track behavioral outcomes for Stage 1 participants. All figures below
            are derived from fictional demonstration data.
          </p>
        </div>
        <button className="btn-ghost inline-flex items-center gap-2" onClick={reseed} data-testid="reseed-btn">
          <RefreshCcw className="w-3.5 h-3.5" /> Reset sample data
        </button>
      </header>

      {/* Gates */}
      <section className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-10" data-testid="gates-section">
        <GateCard
          testId="gate-adherence"
          label="Median On-Time Adherence"
          subLabel="Across behaviorally evaluable participants"
          value={data.gate_adherence.value}
          pass={data.gate_adherence.pass}
          thresholdLabel={`Threshold ≥ ${fmtPct(data.gate_adherence.threshold)}`}
        />
        <GateCard
          testId="gate-preservation"
          label="Preservation Success Rate"
          subLabel="Share of evaluable participants with ≥80% Preservation Rate"
          value={data.gate_preservation.value}
          pass={data.gate_preservation.pass}
          thresholdLabel={`Threshold ≥ ${fmtPct(data.gate_preservation.threshold_cohort)}  ·  per-participant ≥ ${fmtPct(data.gate_preservation.threshold_rate)}`}
        />
      </section>

      {/* Supporting indicators */}
      <section className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
        <MetricCard
          testId="metric-recruited"
          label="Recruited"
          value={data.total_recruited}
          sublabel={`Target 30 · ${data.activated} activated`}
        />
        <MetricCard
          testId="metric-evaluable"
          label="Behaviorally Evaluable"
          value={data.evaluable_count}
          sublabel="≥28 days OR ≥4 commitments due"
          tone="gold"
        />
        <MetricCard
          testId="metric-on-time"
          label="Commitments On-Time"
          value={data.commitments_on_time}
          sublabel={`of ${data.commitments_due} due · ${data.commitments_late_recovered} late/recovered · ${data.commitments_missed} missed`}
        />
        <MetricCard
          testId="metric-behind"
          label="Flagged Behind"
          value={data.behind_count}
          sublabel="Adherence <70% or ≥2 missed in last 4"
          tone={data.behind_count > 0 ? "danger" : "default"}
        />
      </section>

      <section className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
        <MetricCard
          testId="metric-preserved"
          label="Preserved Savings (cohort)"
          value={fmtMoney(data.preserved_total)}
          sublabel="Sum across active participants"
        />
        <MetricCard
          testId="metric-trip-expenses"
          label="Legitimate Trip Expenses"
          value={fmtMoney(data.trip_expenses_total)}
          sublabel="Applied toward participant trips"
        />
        <MetricCard
          testId="metric-leakage"
          label="Non-Trip Leakage"
          value={fmtMoney(data.non_trip_leakage_total)}
          sublabel="Reduces preservation & goal progress"
          tone={data.non_trip_leakage_total > 0 ? "danger" : "default"}
        />
        <MetricCard
          testId="metric-preservation-success"
          label="Preservation Winners"
          value={`${data.preservation_success_count} / ${data.evaluable_count}`}
          sublabel={`Evaluable at ≥${fmtPct(data.gate_preservation.threshold_rate)}`}
          tone="gold"
        />
      </section>

      {/* Behind participants */}
      <section className="mt-10" data-testid="behind-section">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-[22px]" style={{ color: "var(--cream)" }}>
            Participants flagged behind
          </h2>
          <Link
            to="/participants"
            className="text-[12px] inline-flex items-center gap-1"
            style={{ color: "var(--gold-light)" }}
            data-testid="view-all-participants-link"
          >
            View all participants <ArrowUpRight className="w-3 h-3" />
          </Link>
        </div>

        {behind.length === 0 ? (
          <div className="surface p-8 text-center text-[13px]" style={{ color: "var(--cream-dim)" }}>
            No participants currently flagged.
          </div>
        ) : (
          <div className="surface divide-y" style={{ borderColor: "var(--hairline)" }}>
            {behind.map((p) => (
              <Link
                key={p.id}
                to={`/participants/${p.id}`}
                className="flex items-center justify-between px-6 py-4 row-hover"
                data-testid={`behind-row-${p.id}`}
              >
                <div className="flex items-center gap-4 min-w-0">
                  <AlertTriangle className="w-4 h-4" style={{ color: "var(--warn)" }} />
                  <div className="min-w-0">
                    <div className="text-[14px] truncate" style={{ color: "var(--cream)" }}>
                      {p.name}
                    </div>
                    <div className="text-[11px] font-mono-num" style={{ color: "var(--cream-muted)" }}>
                      {p.trip_destination} · {p.cadence}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-8">
                  <div className="text-right">
                    <div className="text-[10px] uppercase tracking-widest font-mono-num" style={{ color: "var(--cream-muted)" }}>
                      Adherence
                    </div>
                    <div className="font-mono-num text-[14px]" style={{ color: "var(--cream)" }}>
                      {fmtPct(p.adherence_rate)}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-[10px] uppercase tracking-widest font-mono-num" style={{ color: "var(--cream-muted)" }}>
                      Missed / Due
                    </div>
                    <div className="font-mono-num text-[14px]" style={{ color: "var(--cream)" }}>
                      {p.commitments_missed} / {p.commitments_due}
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
